/**
 * Founder 2026-10-29 — pick the client for "Add client" by typing an
 * owner email, a name, a domain or an account id, and choosing from a
 * dropdown, instead of pasting a raw account id. Reuses the Accounts
 * list search (`GET /internal/admin/accounts?search=`), which matches
 * name, account_id, domain and owner email. Accounts with no site are
 * found by name, owner email or account id.
 */

import { useEffect, useRef, useState } from 'react';

import type { AdminAccountSummary } from '@sill/types/api';

import { useAdminApi } from '../../api/api-context.js';
import { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export interface AccountPickerProps {
  readonly selected: AdminAccountSummary | null;
  readonly onSelect: (account: AdminAccountSummary | null) => void;
  /** Accounts that can't be picked (the agency itself). */
  readonly excludeAccountIds?: readonly string[];
  readonly disabled?: boolean;
}

export function AccountPicker({ selected, onSelect, excludeAccountIds = [], disabled = false }: AccountPickerProps): JSX.Element {
  const api = useAdminApi();
  const C = useC();
  const [q, setQ] = useState('');
  const [results, setResults] = useState<readonly AdminAccountSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    const mine = ++seq.current;
    setLoading(true);
    const t = setTimeout(() => {
      api.listAccounts({ search: term, limit: 8 })
        .then((r) => {
          if (mine !== seq.current) return;
          setResults(r.accounts.filter((a) => !excludeAccountIds.includes(a.account_id)));
        })
        .catch(() => {
          if (mine === seq.current) setResults([]);
        })
        .finally(() => {
          if (mine === seq.current) setLoading(false);
        });
    }, 250);
    return () => clearTimeout(t);
    // excludeAccountIds is a small literal list from the caller.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, q]);

  if (selected !== null) {
    return (
      <div
        data-testid="account-picker-selected"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          padding: '8px 10px',
          border: `1px solid ${C.border}`,
          borderRadius: 3,
          background: C.bg,
        }}
      >
        <AccountLine account={selected} />
        {!disabled ? (
          <button
            type="button"
            onClick={() => { onSelect(null); setQ(''); }}
            style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, background: 'none', border: 'none', cursor: 'pointer' }}
          >
            Change
          </button>
        ) : null}
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <input
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Owner email, name, domain or account ID"
        aria-label="Find client account"
        autoFocus
        disabled={disabled}
        style={{
          width: '100%',
          fontFamily: monoFont,
          fontSize: 12.5,
          padding: '8px 10px',
          background: C.bg,
          color: C.text,
          border: `1px solid ${C.border}`,
          borderRadius: 3,
          outline: 'none',
          boxSizing: 'border-box',
        }}
      />
      {open && q.trim().length >= 2 ? (
        <div
          role="listbox"
          data-testid="account-picker-results"
          style={{
            marginTop: 4,
            border: `1px solid ${C.border}`,
            borderRadius: 3,
            background: C.surface,
            maxHeight: 260,
            overflowY: 'auto',
          }}
        >
          {loading ? (
            <div style={{ padding: 10, fontFamily: sansFont, fontSize: 12.5, color: C.muted }}>Searching…</div>
          ) : results.length === 0 ? (
            <div style={{ padding: 10, fontFamily: sansFont, fontSize: 12.5, color: C.muted }}>No matching accounts.</div>
          ) : (
            results.map((a) => (
              <button
                key={a.account_id}
                type="button"
                role="option"
                aria-selected={false}
                onClick={() => { onSelect(a); setOpen(false); }}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 10px',
                  background: 'none',
                  border: 'none',
                  borderBottom: `1px solid ${C.borderSoft}`,
                  cursor: 'pointer',
                }}
              >
                <AccountLine account={a} />
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

function AccountLine({ account }: { account: AdminAccountSummary }): JSX.Element {
  const C = useC();
  const domains = account.domains.length > 0 ? account.domains.slice(0, 2).join(', ') : 'no site yet';
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 600 }}>
        {account.name}
        <span style={{ fontWeight: 400, color: C.muted }}> · {account.owner_email ?? 'no owner email'}</span>
      </div>
      <div style={{ fontFamily: monoFont, fontSize: 10.5, color: C.muted, marginTop: 2, overflowWrap: 'anywhere' }}>
        {domains} · {account.account_id}
      </div>
    </div>
  );
}
